import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Extracted from `src/App.js` to keep App lean and reduce re-render/remount churn.
 * Behavior should remain identical to the previous in-App implementation.
 */
export default function useAccessControl({ account, API_URL }) {
  const location = useLocation();
  const navigate = useNavigate();
  const pathnameRef = React.useRef(location.pathname);

  React.useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  const [isAdmin, setIsAdmin] = React.useState(false);
  const [isGuest, setIsGuest] = React.useState(false);
  const [hasAccessEnabled, setHasAccessEnabled] = React.useState(true); // API-driven access flag
  const [guestAllowedPages, setGuestAllowedPages] = React.useState([]);
  const [userAllowedPages, setUserAllowedPages] = React.useState(null); // null => unknown, [] => no specific limits
  const [guestExists, setGuestExists] = React.useState(true);
  const [permissionLevel, setPermissionLevel] = React.useState(null);
  const [accessLoaded, setAccessLoaded] = React.useState(false);

  React.useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    async function checkUserStatus() {
      if (!account) {
        setIsGuest(true);
        setGuestAllowedPages([]);
        setGuestExists(false);
        setIsAdmin(false);

        setAccessLoaded(true);
        return;
      }

      try {
        // First, check guest status
        const guestResp = await fetch(
          `${API_URL}/api/employee/isGuest?email=${encodeURIComponent(account.username)}`,
          { signal }
        );
        const guestResult = await guestResp.json();
        console.log('isGuest API result:', guestResult);

        if (guestResp.ok && guestResult && guestResult.success === true) {
          if (guestResult.exists === false) {
            setIsGuest(true);
            setGuestAllowedPages([]);
            setGuestExists(false);
            setIsAdmin(false);
            setHasAccessEnabled(false); // user not registered => no access
            if (pathnameRef.current !== '/not-registered') navigate('/not-registered', { replace: true });

            setAccessLoaded(true);
            return; // Stop further checks
          }

          const nextIsGuest =
            guestResult.isGuest === true ||
            (typeof guestResult.isGuest === 'string' && guestResult.isGuest.toLowerCase() === 'true') ||
            (typeof guestResult.employmentStatus === 'string' &&
              guestResult.employmentStatus.toLowerCase().includes('guest'));

          // Check if user is a current employee based on employment status
          // (was unused in the original code; keep the compute out to avoid dead code)
          const accessEnabledRaw =
            guestResult.accessEnabled ??
            guestResult.AccessEnabled ??
            guestResult.access_enabled ??
            guestResult.access;

          // Default allow unless API explicitly disables
          let nextHasAccessEnabled = true;
          if (accessEnabledRaw !== undefined && accessEnabledRaw !== null) {
            const normalized =
              typeof accessEnabledRaw === 'string' ? accessEnabledRaw.trim().toLowerCase() : accessEnabledRaw;
            nextHasAccessEnabled =
              normalized === true || normalized === 1 || normalized === '1' || normalized === 'true';
          }

          setIsGuest(nextIsGuest);
          setHasAccessEnabled(nextHasAccessEnabled);
          setGuestAllowedPages(Array.isArray(guestResult.allowedPages) ? guestResult.allowedPages : []);
          setGuestExists(true);

          if (nextIsGuest) {
            setIsAdmin(false); // A guest can never be an admin
          } else {
            // If NOT a guest, check for admin privileges
            const adminResp = await fetch(`${API_URL}/api/checkIfAdmin?email=${account.username}`, { signal });
            const adminResult = await adminResp.json();
            setIsAdmin(adminResult.message === true);
          }

          // Fetch page access for ALL users (not just guests)
          try {
            const accessResp = await fetch(
              `${API_URL}/api/settings/access/get?email=${encodeURIComponent(account.username)}`,
              { signal }
            );
            const accessJson = await accessResp.json().catch(() => ({}));
            const ap =
              accessJson?.allowed_pages ??
              accessJson?.data?.allowed_pages ??
              (Array.isArray(accessJson?.data) ? accessJson?.data : []);
            setUserAllowedPages(Array.isArray(ap) ? ap : []);
            const lvl = accessJson?.permission_level ?? accessJson?.data?.permission_level;
            if (lvl !== undefined && lvl !== null) {
              const pl = Number(lvl);
              setPermissionLevel(pl);
              if (pl === 2 || pl === 3) setIsAdmin(true);
            }
          } catch (_) {
            setUserAllowedPages([]); // default behavior handled downstream
          }

          if (pathnameRef.current === '/not-registered') {
            navigate('/dashboard/personal', { replace: true });
          }

          setAccessLoaded(true);
        } else {
          // Handle failure of guest API call
          setIsGuest(true);
          setGuestAllowedPages([]);
          setGuestExists(false);
          setIsAdmin(false);
          setHasAccessEnabled(false); // Default to no access
          if (pathnameRef.current !== '/not-registered') navigate('/not-registered', { replace: true });

          setAccessLoaded(true);
        }
      } catch (e) {
        // If we aborted because the component unmounted or the request was superseded,
        // do not clobber state (this was causing NotRegistered flicker on navigation).
        if (e?.name === 'AbortError') return;

        setIsGuest(true);
        setGuestAllowedPages([]);
        setGuestExists(false);
        setIsAdmin(false);
        setHasAccessEnabled(false); // Default to no access
        setUserAllowedPages([]);
        if (pathnameRef.current !== '/not-registered') navigate('/not-registered', { replace: true });

        setAccessLoaded(true);
      }
    }

    checkUserStatus();

    return () => controller.abort();
  }, [account, API_URL, navigate]);

  // Redirect ALL users based on allowed pages (defaults to all pages if none configured)
  const allowedPrefixes = React.useMemo(() => {
    if (!guestExists) return [];

    // If user is an admin, they get full access regardless of employment status
    if (isAdmin) {
      const keyToPrefix = {
        dashboard: '/dashboard',
        opportunities: '/opportunities',
        projects: '/projects',
        timesheets: '/timesheets',
        clients: '/clients',
        invoicing: '/invoicing',
        suppliers: '/suppliers',
        profile: '/Profile',
        resourcing: '/resourcing',
        planner: '/resourcing',
        admin: '/Admin',
      };
      const allPrefixes = Object.values(keyToPrefix).filter(Boolean);
      const fromApi =
        Array.isArray(guestAllowedPages) && guestAllowedPages.length > 0
          ? guestAllowedPages
          : Array.isArray(userAllowedPages)
            ? userAllowedPages
            : [];
      let prefixesFromApi =
        fromApi.length > 0
          ? fromApi.map((k) => keyToPrefix[(k || '').toString().trim().toLowerCase()]).filter(Boolean)
          : allPrefixes.slice();
      if (!prefixesFromApi.includes('/Profile')) prefixesFromApi.push('/Profile');
      if (!prefixesFromApi.includes('/Admin')) prefixesFromApi.push('/Admin'); // Admins always get admin access
      if (prefixesFromApi.includes('/invoicing')) {
        if (!prefixesFromApi.includes('/invoicing/raise-invoice')) prefixesFromApi.push('/invoicing/raise-invoice');
        if (!prefixesFromApi.includes('/invoicing/draft-invoice')) prefixesFromApi.push('/invoicing/draft-invoice');
      }

      return prefixesFromApi;
    }

    // If access is disabled by API, restrict to no access (they'll be redirected to no permission page)
    if (!hasAccessEnabled) {
      return []; // No access if API says so
    }

    const keyToPrefix = {
      dashboard: '/dashboard',
      opportunities: '/opportunities',
      projects: '/projects',
      timesheets: '/timesheets',
      clients: '/clients',
      invoicing: '/invoicing',
      suppliers: '/suppliers',
      profile: '/Profile',
      resourcing: '/resourcing',
      planner: '/resourcing',
      admin: '/Admin',
    };
    const allPrefixes = Object.values(keyToPrefix).filter(Boolean);
    const fromApi =
      Array.isArray(guestAllowedPages) && guestAllowedPages.length > 0
        ? guestAllowedPages
        : Array.isArray(userAllowedPages)
          ? userAllowedPages
          : [];
    let prefixesFromApi =
      fromApi.length > 0
        ? fromApi.map((k) => keyToPrefix[(k || '').toString().trim().toLowerCase()]).filter(Boolean)
        : allPrefixes.slice();
    if (!prefixesFromApi.includes('/Profile')) prefixesFromApi.push('/Profile');
    if (prefixesFromApi.includes('/invoicing')) {
      if (!prefixesFromApi.includes('/invoicing/raise-invoice')) prefixesFromApi.push('/invoicing/raise-invoice');
      if (!prefixesFromApi.includes('/invoicing/draft-invoice')) prefixesFromApi.push('/invoicing/draft-invoice');
    }

    return prefixesFromApi;
  }, [guestExists, guestAllowedPages, userAllowedPages, isAdmin, hasAccessEnabled]);

  return {
    isAdmin,
    isGuest,
    guestAllowedPages,
    userAllowedPages,
    guestExists,
    hasAccessEnabled,
    permissionLevel,
    accessLoaded,
    allowedPrefixes,
  };
}


